/**
 * Calculates the interest saved based on overpayment variable
 *
 * @param {number} remainingBalance Remaining balance in pence
 * @param {number} monthlyPayment Monthly payment in pence
 * @param {number} interestRate Interest rate as a float
 * @param {number} monthToClearBy Month number (from today)
 *
 * @return {number} Monthly overpayment required in pence
 *
 */
import { round } from '@homamo/meadow-utils';

import getMonthlyInterestRate from './getMonthlyInterestRate';
import getRemainingTerm from './getRemainingTerm';

const getMonthlyOverpaymentRequired = (args) => {
  // Check arguments
  if (!args.remainingBalance && args.remainingBalance !== 0)
    throw new Error('Remaining balance is required');
  if (!args.monthlyPayment && args.monthlyPayment !== 0)
    throw new Error('Monthly payment is required');
  if (!args.interestRate && args.remainingBalance !== 0)
    throw new Error('Interest rate is required');
  if (!args.monthToClearBy && args.monthToClearBy !== 0)
    throw new Error('Month to clear by is required');

  const { remainingBalance, monthlyPayment, interestRate } = args;

  const remainingTerm = getRemainingTerm({
    remainingBalance,
    monthlyPayment,
    interestRate,
  });

  const monthToClearBy =
    args.monthToClearBy > remainingTerm ? remainingTerm : args.monthToClearBy;

  // Calculate monthly interest rate
  const monthlyInterestRate = getMonthlyInterestRate(interestRate);

  // Calculate increase (e.g 103.92%)
  const increase = 1 + monthlyInterestRate;

  const monthlyOverpayment =
    (remainingBalance * (monthlyInterestRate * increase ** monthToClearBy)) /
      (increase ** monthToClearBy - 1) -
    monthlyPayment;

  return monthlyOverpayment >= 1 ? round(monthlyOverpayment, 0) : 0;
};

export default getMonthlyOverpaymentRequired;
